import { useEffect, useMemo, useRef, useState } from 'react';
import type { MapProps } from '../components/Map/Map.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { debounce } from 'lodash-es';

type MapViewPort = Readonly<{ center: google.maps.LatLngLiteral; bounds: google.maps.LatLngBounds }>;

/**
 * @param searchIdentity Everything that makes one search different from the next. When it changes,
 *   the guest's map interaction is forgotten so the new results are shown in full.
 */
export const useMapControls = ({ searchIdentity }: Readonly<{ searchIdentity: string }>) => {
  // Ref stores the latest viewport immediately (no re-render);
  // debounced state triggers a re-render only after pan/zoom settles.
  const mapViewPortRef = useRef<MapViewPort>();
  const [mapViewPort, setMapViewPortState] = useState<MapViewPort>();
  const isMountedRef = useRef(true);

  const debouncedFlush = useMemo(
    () =>
      debounce((vp: MapViewPort) => {
        if (isMountedRef.current) {
          setMapViewPortState(vp);
        }
      }, 150),
    [],
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
      debouncedFlush.cancel();
    },
    [debouncedFlush],
  );

  const setMapViewPort = useEventCallback((viewport: MapViewPort) => {
    mapViewPortRef.current = viewport;
    debouncedFlush(viewport);
  });

  // True once the guest has deliberately panned or zoomed. Until then the map's bounds are ours,
  // not theirs, so nothing may be filtered by them.
  const [hasUserMovedMap, setHasUserMovedMap] = useState(false);
  const [renderedSearchIdentity, setRenderedSearchIdentity] = useState(searchIdentity);

  // Adjusted during render rather than in an effect: an effect runs after paint, so a search whose
  // results are already cached would render once against the previous viewport before resetting.
  if (renderedSearchIdentity !== searchIdentity) {
    setRenderedSearchIdentity(searchIdentity);
    setHasUserMovedMap(false);
  }

  const markUserMovedMap = useEventCallback(() => {
    setHasUserMovedMap(true);
  });

  const resetUserMovedMap = useEventCallback(() => {
    setHasUserMovedMap(false);
  });

  const [reCenterMap, setReCenterMap] = useState<MapProps['reCenterMap']>({ location: false, markers: false });

  const recenterMapViaMarkers = useEventCallback(() => {
    setReCenterMap(prev => ({ ...prev, markers: true }));
  });

  const recenterMapViaLocation = useEventCallback((isDefaultZoom?: boolean) => {
    setReCenterMap(prev => ({ ...prev, location: true, isDefaultZoom }));
  });

  const resetReCenterConfig = useEventCallback(() => {
    setReCenterMap({ location: false, markers: false });
  });

  const resetLocationRecenterConfig = useEventCallback(() => {
    setReCenterMap(prev => ({ ...prev, location: false }));
  });

  return {
    mapViewPort,
    setMapViewPort,

    hasUserMovedMap,
    markUserMovedMap,
    resetUserMovedMap,

    setReCenterMap,
    reCenterMap,
    recenterMapViaMarkers,
    recenterMapViaLocation,
    resetLocationRecenterConfig,
    resetReCenterConfig,
  };
};
